import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DropdownMenuItem } from 'src/common/DropdownMenu/DropdownMenuItem';
import {
  AssignmentRead,
  DispatchTripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { formatFullName } from 'src/common/util/formatFullName';
import { usePayers } from 'src/features/add-trip/Pricing/usePayers';

import { getExistingMultiloadTripIdsForAssignment } from '../../common/getExistingMultiloadTripIdsForAssignment';
import { getPotentialMultiloadCandidatesForUnassignedTrip } from '../../common/getPotentialMultiloadCandidatesForUnassignedTrip';
import { createEventsFromTrips } from '../../DispatchCalendar/mobiscrollTransformers/createEventsFromTrips';
import { createEventsFromTripWaitTimes } from '../../DispatchCalendar/mobiscrollTransformers/createEventsFromTripWaitTimes';
import { useTripMenuActions } from '../../DispatchCalendar/useTripMenuActions';
import {
  onPreviewTripAssignmentEnd,
  onPreviewTripAssignmentStart,
} from '../../dispatcher.slice';

type DriverAssignmentMenuActionProps = {
  assignment: AssignmentRead;
  trip: DispatchTripRead;
};

export function DriverAssignmentMenuAction({
  assignment,
  trip,
}: DriverAssignmentMenuActionProps) {
  const dispatch = useAppDispatch();
  const menuActions = useTripMenuActions();
  const isAssigned = !!trip.assignment_id;
  const assignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.assigned_trips
  );
  const { payers } = usePayers();

  const createPreviewTrip = (originalTrip: DispatchTripRead) => ({
    ...originalTrip,
    assignment_id: assignment.id,
    id: `${originalTrip.id}-preview`,
  });

  const onMouseEnter = () => {
    const tempTrip = createPreviewTrip(trip);
    let previewEvents = createEventsFromTrips([tempTrip]);

    if (trip.inbound_trip) {
      const tempInboundTrip = createPreviewTrip(
        trip.inbound_trip as DispatchTripRead
      );
      previewEvents = createEventsFromTrips([tempTrip, tempInboundTrip]);

      if (trip.wait_time_start !== null && trip.wait_time_end !== null) {
        const waitTimeEvent = createEventsFromTripWaitTimes([tempTrip])[0];
        previewEvents.push({ ...waitTimeEvent, id: `${trip.id}-wait-preview` });
      }
    }

    dispatch(onPreviewTripAssignmentStart(previewEvents));
  };

  const onClick = () => {
    const { hasPotentialMultiloadTripsAssigned, potentialMultiloadTripIds } =
      getPotentialMultiloadCandidatesForUnassignedTrip(
        assignment.id,
        trip,
        assignedTrips,
        payers?.results
      );

    if (
      hasPotentialMultiloadTripsAssigned &&
      potentialMultiloadTripIds.length > 0
    ) {
      const existingMultiloadTripIds =
        getExistingMultiloadTripIdsForAssignment(
          potentialMultiloadTripIds,
          assignedTrips
        );

      if (existingMultiloadTripIds.length > 0) {
        menuActions.assignToExistingMultiLoad(
          trip.id,
          assignment.id,
          existingMultiloadTripIds
        );
        return;
      }

      menuActions.multiloadAssign(
        trip.id,
        assignment.id,
        potentialMultiloadTripIds
      );
      return;
    }

    if (isAssigned) {
      menuActions.reassign(
        trip.id,
        assignment.id,
        trip.status,
        trip.multiload_id
      );
      return;
    }

    menuActions.assign(trip.id, assignment.id, trip.status);
  };

  return (
    <>
      <DropdownMenuItem
        key={assignment.id}
        data-testid={`driver-assignment-menu-item-${assignment.id}`}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={() => {
          dispatch(onPreviewTripAssignmentEnd());
        }}
      >
        {formatFullName(assignment.driver)}
      </DropdownMenuItem>
    </>
  );
}
